import React, {
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Select,
  Input,
  Space,
  Avatar,
  Tag,
  Statistic,
  Spin,
  Alert,
  Modal,
  Button,
  Divider,
  Empty,
  message,
} from "antd";

import {
  TrophyOutlined,
  CrownOutlined,
  TeamOutlined,
  SearchOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  BarChartOutlined,
  RiseOutlined,
  StarFilled,
  SafetyCertificateOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { PDFDownloadLink } from "@react-pdf/renderer";
import { useAcademicYear } from "../../AcademicYearContext";
import api from "../../api/api";
import TopStudentPerPeriodPDF from "./TopStudentPerPeriodPDF";

const { Title, Text } = Typography;
const { Option } = Select;


/* ============================================================
   Excellent Kids Foundation School System COLOR SYSTEM
============================================================ */

const COLORS = {
  deepNavy: "#06172D",
  navy: "#092443",
  royalNavy: "#10375F",
  blue: "#174C78",

  gold: "#C9A44C",
  champagne: "#DCC27A",
  brightGold: "#E9D79E",
  antiqueGold: "#9C7930",

  white: "#FFFFFF",
  paper: "#FBFAF6",
  softNavy: "#EEF3F8",
  softGold: "#FBF5E5",

  text: "#182431",
  muted: "#697581",

  border: "#D9DEE4",
  goldBorder: "#E1D2A9",

  success: "#3C8C5A",
  warning: "#C98A24",
  danger: "#B54B4B",

  silver: "#8C969F",
  bronze: "#9A6743",
};


/* ============================================================
   PAGE
============================================================ */

const TopStudentPerPeriod = () => {

  const { selectedYear } =
    useAcademicYear();


  const [gradeClasses, setGradeClasses] =
    useState([]);

  const [periods, setPeriods] =
    useState([]);

  const [students, setStudents] =
    useState([]);

  const [reportData, setReportData] =
    useState(null);


  const [selectedClass, setSelectedClass] =
    useState();

  const [selectedPeriod, setSelectedPeriod] =
    useState();


  const [loading, setLoading] =
    useState(false);

  const [loadingClasses, setLoadingClasses] =
    useState(false);

  const [loadingPeriods, setLoadingPeriods] =
    useState(false);


  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");


  const [showPDFModal, setShowPDFModal] =
    useState(false);


  /* ==========================================================
     LOAD GRADE CLASSES
  ========================================================== */

  const loadGradeClasses =
    useCallback(async () => {

      try {

        setLoadingClasses(true);

        const response =
          await api.get(
            "/api/users/grade-classes/"
          );

        setGradeClasses(
          response.data.results ||
          response.data
        );

      } catch (error) {

        console.error(error);

        message.error(
          "Unable to load grade classes."
        );

      } finally {

        setLoadingClasses(false);

      }

    }, []);


  /* ==========================================================
     LOAD PERIODS
  ========================================================== */

  const loadPeriods =
    useCallback(async () => {

      try {

        setLoadingPeriods(true);

        const response =
          await api.get(
            "/api/users/periods/"
          );

        setPeriods(
          response.data.results ||
          response.data
        );

      } catch (error) {

        console.error(error);

        message.error(
          "Unable to load academic periods."
        );

      } finally {

        setLoadingPeriods(false);

      }

    }, []);


  /* ==========================================================
     LOAD TOP STUDENTS
  ========================================================== */

  const loadTopStudents =
    useCallback(async () => {

      if (
        !selectedClass ||
        !selectedPeriod
      ) {

        setStudents([]);

        setReportData(null);

        return;
      }


      try {

        setLoading(true);

        setError("");


        const response =
          await api.get(
            "/top-students/",
            {
              params: {
                grade_class:
                  selectedClass,

                period:
                  selectedPeriod,

                search,
              },
            }
          );


        const data =
          response.data;


        const results =
          data.results || [];


        setStudents(results);


        setReportData({
          academic_year:
            data.academic_year,

          grade_class:
            results.length > 0
              ? results[0].grade_class
              : "",

          period:
            results.length > 0
              ? results[0].period
              : "",

          students: results,
        });


      } catch (error) {

        console.error(error);

        setStudents([]);

        setReportData(null);

        setError(
          "Unable to load top students."
        );

      } finally {

        setLoading(false);

      }

    }, [
      selectedClass,
      selectedPeriod,
      search,
    ]);


  /* ==========================================================
     INITIAL LOAD
  ========================================================== */

  useEffect(() => {

    loadGradeClasses();

    loadPeriods();

  }, [
    loadGradeClasses,
    loadPeriods,
  ]);


  useEffect(() => {

    loadTopStudents();

  }, [
    loadTopStudents,
  ]);


  /* ==========================================================
     HANDLERS
  ========================================================== */

  const handleGradeClassChange =
    (value) => {

      setSelectedClass(value);

    };


  const handlePeriodChange =
    (value) => {

      setSelectedPeriod(value);

    };


  const handleSearch =
    (e) => {

      setSearch(
        e.target.value
      );

    };


  const handleDownloadReport =
    () => {

      if (!students.length) {

        message.warning(
          "No student rankings available."
        );

        return;
      }

      setShowPDFModal(true);

    };


  const handleReset =
    () => {

      setSelectedClass(undefined);

      setSelectedPeriod(undefined);

      setSearch("");

      setStudents([]);

      setReportData(null);

      setError("");

    };


  /* ==========================================================
     RANKING DATA
  ========================================================== */

  const dux =
    students.length
      ? students[0]
      : null;


  const secondPlace =
    students.length > 1
      ? students[1]
      : null;


  const thirdPlace =
    students.length > 2
      ? students[2]
      : null;


  const selectedClassName =
    gradeClasses.find(
      (item) =>
        item.id === selectedClass
    )?.name || "--";


  const selectedPeriodName =
    periods.find(
      (item) =>
        item.id === selectedPeriod
    )?.name || "--";


  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "24px",
        background:
          "linear-gradient(180deg,#F4F6F8 0%,#EEF1F4 100%)",
      }}
    >

      {/* ======================================================
          HERO
      ====================================================== */}

      <Card
        bordered={false}
        style={{
          marginBottom: 22,
          borderRadius: 20,
          overflow: "hidden",
          background:
            `linear-gradient(135deg,
              ${COLORS.deepNavy} 0%,
              ${COLORS.navy} 55%,
              ${COLORS.royalNavy} 100%)`,
          boxShadow:
            "0 14px 35px rgba(6,23,45,0.16)",
        }}
        bodyStyle={{
          padding: 0,
        }}
      >

        <div
          style={{
            position: "relative",
            padding:
              "30px 34px 28px",
            overflow: "hidden",
          }}
        >

          {/* Decorative geometry */}

          <div
            style={{
              position: "absolute",
              width: 230,
              height: 230,
              right: -70,
              top: -100,
              border:
                `1px solid ${COLORS.champagne}`,
              opacity: 0.18,
              transform: "rotate(45deg)",
            }}
          />

          <div
            style={{
              position: "absolute",
              width: 150,
              height: 150,
              right: 80,
              bottom: -105,
              border:
                `1px solid ${COLORS.gold}`,
              opacity: 0.13,
              transform: "rotate(45deg)",
            }}
          />

          <div
            style={{
              position: "absolute",
              right: 28,
              top: 28,
              width: 8,
              height: 8,
              background:
                COLORS.champagne,
              transform: "rotate(45deg)",
              boxShadow:
                `0 0 0 5px rgba(220,194,122,0.08)`,
            }}
          />


          <Row
            align="middle"
            justify="space-between"
            gutter={[24, 24]}
          >

            <Col
              xs={24}
              lg={18}
            >

              <Space
                direction="vertical"
                size={5}
              >

                <Tag
                  style={{
                    margin: 0,
                    border: 0,
                    borderRadius: 20,
                    padding:
                      "4px 13px",
                    background:
                      "rgba(220,194,122,0.14)",
                    color:
                      COLORS.champagne,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.1,
                  }}
                >
                  Excellent Kids Foundation
                </Tag>


                <Title
                  level={2}
                  style={{
                    margin: 0,
                    color:
                      COLORS.white,
                    fontSize: 30,
                    fontWeight: 800,
                    letterSpacing: -0.5,
                  }}
                >
                  <TrophyOutlined
                    style={{
                      color:
                        COLORS.champagne,
                      marginRight: 12,
                    }}
                  />

                  Top Students Per Period
                </Title>


                <Text
                  style={{
                    color:
                      "rgba(255,255,255,0.82)",
                    fontSize: 14,
                  }}
                >
                  Academic performance,
                  recognition and class
                  ranking overview.
                </Text>


                <Space
                  size={18}
                  wrap
                  style={{
                    marginTop: 7,
                  }}
                >

                  <Space size={7}>
                    <CalendarOutlined
                      style={{
                        color:
                          COLORS.champagne,
                      }}
                    />

                    <Text
                      style={{
                        color:
                          COLORS.white,
                      }}
                    >
                      Academic Year:
                    </Text>

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.champagne,
                      }}
                    >
                      {selectedYear?.name ||
                        "Loading..."}
                    </Text>
                  </Space>


                  {selectedClassName !==
                    "--" && (
                    <Space size={7}>
                      <ApartmentOutlined
                        style={{
                          color:
                            COLORS.champagne,
                        }}
                      />

                      <Text
                        style={{
                          color:
                            "rgba(255,255,255,0.82)",
                        }}
                      >
                        {selectedClassName}
                      </Text>
                    </Space>
                  )}

                </Space>

              </Space>

            </Col>


            <Col
              xs={24}
              lg={6}
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
              }}
            >

              <div
                style={{
                  width: 105,
                  height: 105,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  background:
                    "linear-gradient(145deg,#DCC27A,#A88332)",
                  boxShadow:
                    "0 10px 28px rgba(0,0,0,0.22)",
                  border:
                    "5px solid rgba(255,255,255,0.12)",
                }}
              >

                <TrophyOutlined
                  style={{
                    fontSize: 46,
                    color:
                      COLORS.deepNavy,
                  }}
                />

              </div>

            </Col>

          </Row>

        </div>

      </Card>


      {/* ======================================================
          STATISTICS
      ====================================================== */}

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 22,
        }}
      >

        <Col
          xs={24}
          sm={12}
          lg={6}
        >

          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              height: "100%",
              borderTop:
                `3px solid ${COLORS.champagne}`,
              boxShadow:
                "0 7px 20px rgba(6,23,45,0.07)",
            }}
          >

            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight: 600,
                  }}
                >
                  Class Dux
                </Text>
              }
              value={
                dux
                  ? dux.student_name
                  : "--"
              }
              prefix={
                <CrownOutlined
                  style={{
                    color:
                      COLORS.gold,
                  }}
                />
              }
              valueStyle={{
                color:
                  COLORS.deepNavy,
                fontSize: 17,
                fontWeight: 700,
              }}
            />

            <Divider
              style={{
                margin: "11px 0",
              }}
            />

            <Tag
              style={{
                margin: 0,
                borderRadius: 20,
                background:
                  COLORS.softGold,
                border:
                  `1px solid ${COLORS.goldBorder}`,
                color:
                  COLORS.antiqueGold,
                fontWeight: 700,
              }}
            >
              {dux
                ? `${dux.average_score}%`
                : "No Data"}
            </Tag>

          </Card>

        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >

          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              height: "100%",
              boxShadow:
                "0 7px 20px rgba(6,23,45,0.07)",
            }}
          >

            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight: 600,
                  }}
                >
                  Students Ranked
                </Text>
              }
              value={
                students.length
              }
              prefix={
                <TeamOutlined
                  style={{
                    color:
                      COLORS.royalNavy,
                  }}
                />
              }
              valueStyle={{
                color:
                  COLORS.deepNavy,
                fontWeight: 800,
              }}
            />

            <Text
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 11,
                color:
                  COLORS.muted,
              }}
            >
              Students included in
              this ranking
            </Text>

          </Card>

        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >

          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              height: "100%",
              boxShadow:
                "0 7px 20px rgba(6,23,45,0.07)",
            }}
          >

            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight: 600,
                  }}
                >
                  Selected Class
                </Text>
              }
              value={
                selectedClassName
              }
              prefix={
                <BarChartOutlined
                  style={{
                    color:
                      COLORS.royalNavy,
                  }}
                />
              }
              valueStyle={{
                color:
                  COLORS.deepNavy,
                fontSize: 16,
                fontWeight: 700,
              }}
            />

            <Text
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 11,
                color:
                  COLORS.muted,
              }}
            >
              Current ranking group
            </Text>

          </Card>

        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >

          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              height: "100%",
              boxShadow:
                "0 7px 20px rgba(6,23,45,0.07)",
            }}
          >

            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight: 600,
                  }}
                >
                  Report Status
                </Text>
              }
              value={
                students.length
                  ? "Ready"
                  : "Waiting"
              }
              prefix={
                <RiseOutlined
                  style={{
                    color:
                      students.length
                        ? COLORS.success
                        : COLORS.warning,
                  }}
                />
              }
              valueStyle={{
                color:
                  students.length
                    ? COLORS.success
                    : COLORS.warning,
                fontWeight: 800,
              }}
            />

            <Text
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 11,
                color:
                  COLORS.muted,
              }}
            >
              PDF report availability
            </Text>

          </Card>

        </Col>

      </Row>


      {/* ======================================================
          FILTER PANEL
      ====================================================== */}

      <Card
        bordered={false}
        style={{
          borderRadius: 18,
          marginBottom: 22,
          boxShadow:
            "0 7px 20px rgba(6,23,45,0.07)",
        }}
        bodyStyle={{
          padding: 20,
        }}
      >

        <Row
          align="middle"
          justify="space-between"
          gutter={[18, 18]}
        >

          <Col
            xs={24}
            lg={6}
          >

            <Space
              direction="vertical"
              size={1}
            >

              <Text
                strong
                style={{
                  color:
                    COLORS.deepNavy,
                  fontSize: 16,
                }}
              >
                Ranking Filters
              </Text>

              <Text
                style={{
                  color:
                    COLORS.muted,
                  fontSize: 12,
                }}
              >
                Select the class and
                academic period.
              </Text>

            </Space>

          </Col>


          <Col
            xs={24}
            sm={12}
            lg={6}
          >

            <Text
              strong
              style={{
                color:
                  COLORS.text,
                fontSize: 12,
              }}
            >
              Grade Class
            </Text>

            <Select
              showSearch
              allowClear
              placeholder="Select Grade Class"
              value={selectedClass}
              loading={loadingClasses}
              onChange={
                handleGradeClassChange
              }
              style={{
                width: "100%",
                marginTop: 7,
              }}
              optionFilterProp="children"
              suffixIcon={
                <ApartmentOutlined
                  style={{
                    color:
                      COLORS.gold,
                  }}
                />
              }
            >

              {gradeClasses.map(
                (item) => (
                  <Option
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </Option>
                )
              )}

            </Select>

          </Col>


          <Col
            xs={24}
            sm={12}
            lg={6}
          >

            <Text
              strong
              style={{
                color:
                  COLORS.text,
                fontSize: 12,
              }}
            >
              Academic Period
            </Text>

            <Select
              showSearch
              allowClear
              placeholder="Select Period"
              value={selectedPeriod}
              loading={loadingPeriods}
              onChange={
                handlePeriodChange
              }
              style={{
                width: "100%",
                marginTop: 7,
              }}
              optionFilterProp="children"
              suffixIcon={
                <CalendarOutlined
                  style={{
                    color:
                      COLORS.gold,
                  }}
                />
              }
            >

              {periods.map(
                (item) => (
                  <Option
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </Option>
                )
              )}

            </Select>

          </Col>


          <Col
            xs={24}
            sm={12}
            lg={6}
          >

            <Text
              strong
              style={{
                color:
                  COLORS.text,
                fontSize: 12,
              }}
            >
              Search Student
            </Text>

            <Input
              allowClear
              prefix={
                <SearchOutlined
                  style={{
                    color:
                      COLORS.gold,
                  }}
                />
              }
              placeholder="Search student..."
              value={search}
              onChange={
                handleSearch
              }
              style={{
                marginTop: 7,
              }}
            />

          </Col>


          <Col
            xs={24}
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
            }}
          >

            <Button
              icon={
                <ReloadOutlined />
              }
              onClick={
                handleReset
              }
              style={{
                borderRadius: 8,
                color:
                  COLORS.deepNavy,
                borderColor:
                  COLORS.border,
              }}
            >
              Reset Filters
            </Button>

          </Col>

        </Row>

      </Card>


      {/* ======================================================
          PODIUM
      ====================================================== */}

      {students.length > 0 && (

        <Card
          bordered={false}
          style={{
            borderRadius: 20,
            marginBottom: 22,
            overflow: "hidden",
            background:
              "linear-gradient(145deg,#FFFFFF,#F7F8FA)",
            boxShadow:
              "0 9px 26px rgba(6,23,45,0.08)",
          }}
          bodyStyle={{
            padding: 24,
          }}
        >

          <div
            style={{
              textAlign: "center",
              marginBottom: 20,
            }}
          >

            <Tag
              style={{
                border: 0,
                background:
                  COLORS.softGold,
                color:
                  COLORS.antiqueGold,
                fontWeight: 700,
                letterSpacing: 1,
                borderRadius: 20,
                padding:
                  "4px 13px",
              }}
            >
              <StarFilled
                style={{
                  marginRight: 6,
                }}
              />
              TOP PERFORMERS
            </Tag>


            <Title
              level={3}
              style={{
                margin:
                  "9px 0 3px",
                color:
                  COLORS.deepNavy,
              }}
            >
              Academic Recognition
            </Title>


            <Text
              style={{
                color:
                  COLORS.muted,
              }}
            >
              Leading students for{" "}
              <strong>
                {selectedPeriodName}
              </strong>
            </Text>

          </div>


          <Row
            gutter={[18, 18]}
            align="bottom"
            justify="center"
          >

            {/* SECOND PLACE */}

            <Col
              xs={24}
              md={8}
              style={{
                order: 1,
              }}
            >

              <div
                style={{
                  textAlign: "center",
                  padding:
                    "24px 16px 20px",
                  borderRadius: 18,
                  background:
                    "linear-gradient(180deg,#F4F6F8,#FFFFFF)",
                  border:
                    `1px solid ${COLORS.border}`,
                }}
              >

                <div
                  style={{
                    position: "relative",
                    display:
                      "inline-flex",
                    marginBottom: 12,
                  }}
                >

                  <Avatar
                    size={74}
                    style={{
                      background:
                        "linear-gradient(145deg,#B7BEC5,#7F8992)",
                      color:
                        COLORS.white,
                      fontSize: 25,
                      fontWeight: 800,
                      border:
                        "4px solid #E4E7EA",
                    }}
                  >
                    2
                  </Avatar>

                </div>


                <Text
                  strong
                  style={{
                    display: "block",
                    color:
                      COLORS.deepNavy,
                    fontSize: 17,
                  }}
                >
                  {secondPlace?.student_name ||
                    "--"}
                </Text>


                <Text
                  style={{
                    display: "block",
                    color:
                      COLORS.muted,
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Second Place
                </Text>


                <Tag
                  style={{
                    marginTop: 11,
                    borderRadius: 20,
                    background:
                      "#F0F2F4",
                    color:
                      "#69737C",
                    border:
                      "1px solid #D7DCE0",
                    fontWeight: 700,
                  }}
                >
                  {secondPlace?.average_score ||
                    "--"}
                  %
                </Tag>

              </div>

            </Col>


            {/* FIRST PLACE */}

            <Col
              xs={24}
              md={8}
              style={{
                order: 2,
              }}
            >

              <div
                style={{
                  textAlign: "center",
                  padding:
                    "30px 16px 24px",
                  borderRadius: 20,
                  background:
                    `linear-gradient(145deg,
                      ${COLORS.deepNavy},
                      ${COLORS.navy})`,
                  border:
                    `2px solid ${COLORS.champagne}`,
                  boxShadow:
                    "0 13px 32px rgba(6,23,45,0.20)",
                  transform:
                    "translateY(-4px)",
                }}
              >

                <div
                  style={{
                    display:
                      "inline-flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background:
                      COLORS.champagne,
                    marginBottom: 8,
                  }}
                >

                  <CrownOutlined
                    style={{
                      color:
                        COLORS.deepNavy,
                      fontSize: 19,
                    }}
                  />

                </div>


                <div>

                  <Avatar
                    size={92}
                    style={{
                      background:
                        "linear-gradient(145deg,#DCC27A,#A78335)",
                      color:
                        COLORS.deepNavy,
                      fontSize: 30,
                      fontWeight: 900,
                      border:
                        "5px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    {dux?.student_name
                      ?.charAt(0) || "1"}
                  </Avatar>

                </div>


                <Title
                  level={4}
                  style={{
                    color:
                      COLORS.white,
                    margin:
                      "13px 0 3px",
                    fontSize: 20,
                  }}
                >
                  {dux?.student_name ||
                    "--"}
                </Title>


                <Text
                  style={{
                    color:
                      COLORS.champagne,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  CLASS DUX
                </Text>


                <div
                  style={{
                    marginTop: 13,
                  }}
                >

                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 20,
                      border:
                        "1px solid rgba(220,194,122,0.45)",
                      background:
                        "rgba(220,194,122,0.12)",
                      color:
                        COLORS.brightGold,
                      fontSize: 14,
                      fontWeight: 800,
                      padding:
                        "4px 14px",
                    }}
                  >
                    {dux?.average_score ||
                      "--"}
                    %
                  </Tag>

                </div>

              </div>

            </Col>


            {/* THIRD PLACE */}

            <Col
              xs={24}
              md={8}
              style={{
                order: 3,
              }}
            >

              <div
                style={{
                  textAlign: "center",
                  padding:
                    "24px 16px 20px",
                  borderRadius: 18,
                  background:
                    "linear-gradient(180deg,#F4F6F8,#FFFFFF)",
                  border:
                    `1px solid ${COLORS.border}`,
                }}
              >

                <Avatar
                  size={74}
                  style={{
                    background:
                      "linear-gradient(145deg,#C18C64,#8B5A38)",
                    color:
                      COLORS.white,
                    fontSize: 25,
                    fontWeight: 800,
                    border:
                      "4px solid #E8DDD5",
                    marginBottom: 12,
                  }}
                >
                  3
                </Avatar>


                <Text
                  strong
                  style={{
                    display: "block",
                    color:
                      COLORS.deepNavy,
                    fontSize: 17,
                  }}
                >
                  {thirdPlace?.student_name ||
                    "--"}
                </Text>


                <Text
                  style={{
                    display: "block",
                    color:
                      COLORS.muted,
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Third Place
                </Text>


                <Tag
                  style={{
                    marginTop: 11,
                    borderRadius: 20,
                    background:
                      "#F8EEE7",
                    color:
                      COLORS.bronze,
                    border:
                      "1px solid #E6D4C5",
                    fontWeight: 700,
                  }}
                >
                  {thirdPlace?.average_score ||
                    "--"}
                  %
                </Tag>

              </div>

            </Col>

          </Row>

        </Card>

      )}


      {/* ======================================================
          RANKING TABLE
      ====================================================== */}

      <Card
        bordered={false}
        title={
          <Space size={10}>

            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                background:
                  COLORS.softGold,
              }}
            >

              <StarFilled
                style={{
                  color:
                    COLORS.gold,
                  fontSize: 17,
                }}
              />

            </div>


            <div>

              <Text
                strong
                style={{
                  display: "block",
                  color:
                    COLORS.deepNavy,
                  fontSize: 17,
                }}
              >
                Student Rankings
              </Text>

              <Text
                style={{
                  fontSize: 11,
                  color:
                    COLORS.muted,
                  fontWeight: 400,
                }}
              >
                Academic performance
                leaderboard
              </Text>

            </div>

          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={
              <DownloadOutlined />
            }
            onClick={
              handleDownloadReport
            }
            disabled={
              !students.length
            }
            style={{
              borderRadius: 9,
              height: 38,
              background:
                COLORS.deepNavy,
              borderColor:
                COLORS.deepNavy,
              boxShadow:
                "0 5px 13px rgba(6,23,45,0.16)",
            }}
          >
            Download PDF
          </Button>
        }
        style={{
          borderRadius: 18,
          boxShadow:
            "0 8px 25px rgba(6,23,45,0.07)",
        }}
        bodyStyle={{
          padding: 0,
        }}
      >

        {loading ? (

          <div
            style={{
              minHeight: 280,
              display:
                "flex",
              alignItems:
                "center",
              justifyContent:
                "center",
              flexDirection:
                "column",
              gap: 14,
            }}
          >

            <Spin
              size="large"
              style={{
                color:
                  COLORS.deepNavy,
              }}
            />

            <Text
              style={{
                color:
                  COLORS.muted,
              }}
            >
              Loading student rankings...
            </Text>

          </div>

        ) : error ? (

          <div
            style={{
              padding: 25,
            }}
          >

            <Alert
              type="error"
              showIcon
              message="Unable to load rankings"
              description={error}
              action={
                <Button
                  size="small"
                  onClick={
                    loadTopStudents
                  }
                >
                  Retry
                </Button>
              }
            />

          </div>

        ) : students.length === 0 ? (

          <div
            style={{
              padding:
                "55px 20px",
            }}
          >

            <Empty
              image={
                <div
                  style={{
                    width: 76,
                    height: 76,
                    margin: "0 auto",
                    borderRadius: "50%",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    background:
                      COLORS.softGold,
                  }}
                >

                  <TrophyOutlined
                    style={{
                      fontSize: 32,
                      color:
                        COLORS.gold,
                    }}
                  />

                </div>
              }
              description={
                <Space
                  direction="vertical"
                  size={2}
                >

                  <Text
                    strong
                    style={{
                      color:
                        COLORS.deepNavy,
                    }}
                  >
                    No ranking data available
                  </Text>

                  <Text
                    style={{
                      color:
                        COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    Select a grade class
                    and academic period
                    to view student
                    rankings.
                  </Text>

                </Space>
              }
            />

          </div>

        ) : (

          <Table
            columns={[

              {
                title: "RANK",
                dataIndex: "rank",
                key: "rank",
                width: 90,
                align: "center",

                render: (rank) => {

                  const isFirst =
                    rank === 1;

                  const isSecond =
                    rank === 2;

                  const isThird =
                    rank === 3;


                  return (
                    <div
                      style={{
                        display:
                          "flex",
                        justifyContent:
                          "center",
                      }}
                    >

                      <Avatar
                        size={
                          isFirst
                            ? 38
                            : 34
                        }
                        style={{
                          background:
                            isFirst
                              ? `linear-gradient(145deg,
                                  ${COLORS.champagne},
                                  ${COLORS.antiqueGold})`
                              : isSecond
                              ? "#8C969F"
                              : isThird
                              ? COLORS.bronze
                              : COLORS.deepNavy,

                          color:
                            isFirst
                              ? COLORS.deepNavy
                              : COLORS.white,

                          fontWeight: 800,

                          border:
                            isFirst
                              ? `2px solid ${COLORS.brightGold}`
                              : "none",
                        }}
                      >
                        {rank}
                      </Avatar>

                    </div>
                  );
                },
              },


              {
                title: "STUDENT",
                dataIndex:
                  "student_name",
                key: "student_name",

                render: (name) => (

                  <Space size={10}>

                    <Avatar
                      size={38}
                      style={{
                        background:
                          COLORS.deepNavy,
                        color:
                          COLORS.champagne,
                        fontWeight: 800,
                      }}
                    >
                      {name
                        ?.charAt(0)
                        ?.toUpperCase()}
                    </Avatar>


                    <div>

                      <Text
                        strong
                        style={{
                          color:
                            COLORS.deepNavy,
                          fontSize: 13,
                        }}
                      >
                        {name}
                      </Text>

                    </div>

                  </Space>
                ),
              },


              {
                title: "GRADE CLASS",
                dataIndex:
                  "grade_class",
                key: "grade_class",
                align: "center",

                render: (value) => (

                  <Tag
                    style={{
                      borderRadius: 20,
                      background:
                        COLORS.softNavy,
                      color:
                        COLORS.royalNavy,
                      border:
                        `1px solid #D5DFE9`,
                      fontWeight: 600,
                    }}
                  >
                    {value}
                  </Tag>

                ),
              },


              {
                title: "PERIOD",
                dataIndex:
                  "period",
                key: "period",
                align: "center",

                render: (value) => (

                  <Tag
                    style={{
                      borderRadius: 20,
                      background:
                        COLORS.softGold,
                      color:
                        COLORS.antiqueGold,
                      border:
                        `1px solid ${COLORS.goldBorder}`,
                      fontWeight: 600,
                    }}
                  >
                    {value}
                  </Tag>

                ),
              },


              {
                title: "AVERAGE",
                dataIndex:
                  "average_score",
                key: "average_score",
                align: "center",

                sorter: (
                  a,
                  b
                ) =>
                  a.average_score -
                  b.average_score,

                render: (value) => {

                  let background =
                    COLORS.softNavy;

                  let color =
                    COLORS.royalNavy;

                  if (
                    value >= 95
                  ) {
                    background =
                      "#EAF5EE";

                    color =
                      COLORS.success;
                  } else if (
                    value >= 90
                  ) {
                    background =
                      COLORS.softNavy;

                    color =
                      COLORS.royalNavy;
                  } else if (
                    value >= 80
                  ) {
                    background =
                      COLORS.softGold;

                    color =
                      COLORS.antiqueGold;
                  } else {
                    background =
                      "#FBEFE9";

                    color =
                      COLORS.warning;
                  }


                  return (
                    <Tag
                      style={{
                        minWidth: 62,
                        textAlign:
                          "center",
                        borderRadius:
                          20,
                        background,
                        color,
                        border:
                          "none",
                        fontWeight: 800,
                      }}
                    >
                      {value}%
                    </Tag>
                  );
                },
              },


              {
                title: "GRADE",
                dataIndex:
                  "alphabet_grade",
                key: "alphabet_grade",
                align: "center",

                render: (grade) => {

                  const isExcellent =
                    grade === "A+" ||
                    grade === "A";

                  const isGood =
                    grade === "B+" ||
                    grade === "B";


                  return (
                    <Tag
                      style={{
                        minWidth: 42,
                        textAlign:
                          "center",
                        borderRadius:
                          20,
                        background:
                          isExcellent
                            ? COLORS.softGold
                            : isGood
                            ? COLORS.softNavy
                            : "#F8EEE7",
                        color:
                          isExcellent
                            ? COLORS.antiqueGold
                            : isGood
                            ? COLORS.royalNavy
                            : COLORS.bronze,
                        border:
                          "none",
                        fontWeight: 800,
                      }}
                    >
                      {grade}
                    </Tag>
                  );
                },
              },

            ]}

            dataSource={
              students
            }

            rowKey="student_id"

            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal:
                (total) =>
                  `Total Ranked Students: ${total}`,
            }}

            scroll={{
              x: 850,
            }}

            bordered={false}

            rowClassName={() =>
              "excellent-kids-ranking-row"
            }
          />

        )}

      </Card>


      {/* ======================================================
          PDF MODAL
      ====================================================== */}

      <Modal
        title={
          <Space>

            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                background:
                  COLORS.softGold,
              }}
            >

              <FilePdfOutlined
                style={{
                  color:
                    COLORS.gold,
                  fontSize: 18,
                }}
              />

            </div>


            <div>

              <Text
                strong
                style={{
                  display: "block",
                  color:
                    COLORS.deepNavy,
                  fontSize: 16,
                }}
              >
                Download Top Students Report
              </Text>

              <Text
                style={{
                  fontSize: 11,
                  color:
                    COLORS.muted,
                }}
              >
                Official academic ranking report
              </Text>

            </div>

          </Space>
        }
        open={
          showPDFModal
        }
        onCancel={() =>
          setShowPDFModal(false)
        }
        footer={null}
        centered
        width={520}
      >

        <div
          style={{
            padding:
              "8px 0 4px",
          }}
        >

          <div
            style={{
              padding: 18,
              borderRadius: 14,
              background:
                COLORS.softNavy,
              border:
                "1px solid #DCE4EC",
              marginBottom: 18,
            }}
          >

            <Row
              gutter={[
                16,
                14,
              ]}
            >

              <Col span={12}>

                <Text
                  style={{
                    display:
                      "block",
                    fontSize: 10,
                    color:
                      COLORS.muted,
                  }}
                >
                  GRADE CLASS
                </Text>

                <Text
                  strong
                  style={{
                    color:
                      COLORS.deepNavy,
                  }}
                >
                  {reportData?.grade_class ||
                    "--"}
                </Text>

              </Col>


              <Col span={12}>

                <Text
                  style={{
                    display:
                      "block",
                    fontSize: 10,
                    color:
                      COLORS.muted,
                  }}
                >
                  PERIOD
                </Text>

                <Text
                  strong
                  style={{
                    color:
                      COLORS.deepNavy,
                  }}
                >
                  {reportData?.period ||
                    "--"}
                </Text>

              </Col>


              <Col span={24}>

                <Divider
                  style={{
                    margin:
                      "1px 0 4px",
                  }}
                />

                <Space>
                  <SafetyCertificateOutlined
                    style={{
                      color:
                        COLORS.gold,
                    }}
                  />

                  <Text
                    style={{
                      color:
                        COLORS.muted,
                      fontSize: 12,
                    }}
                  >
                    {students.length} ranked
                    student
                    {students.length ===
                    1
                      ? ""
                      : "s"}{" "}
                    included
                  </Text>
                </Space>

              </Col>

            </Row>

          </div>


          <Text
            style={{
              color:
                COLORS.muted,
              lineHeight: 1.6,
              fontSize: 13,
            }}
          >
            Generate the official Top
            Students Per Period report
            for the selected academic
            class and period.
          </Text>


          <Divider />


          <Space
            style={{
              width: "100%",
              justifyContent:
                "flex-end",
            }}
          >

            <Button
              onClick={() =>
                setShowPDFModal(false)
              }
              style={{
                borderRadius: 8,
              }}
            >
              Cancel
            </Button>


            <PDFDownloadLink
              document={
                <TopStudentPerPeriodPDF
                  data={reportData}
                />
              }
              fileName={`Top_Students_${reportData?.grade_class}_${reportData?.period}.pdf`}
            >
              {({
                loading,
              }) => (

                <Button
                  type="primary"
                  icon={
                    <DownloadOutlined />
                  }
                  loading={loading}
                  style={{
                    borderRadius: 8,
                    background:
                      COLORS.deepNavy,
                    borderColor:
                      COLORS.deepNavy,
                  }}
                >
                  Download PDF
                </Button>

              )}
            </PDFDownloadLink>

          </Space>

        </div>

      </Modal>


      {/* ======================================================
          TABLE POLISH
      ====================================================== */}

      <style>
        {`
          .excellent-kids-ranking-row > td {
            padding-top: 13px !important;
            padding-bottom: 13px !important;
            border-bottom: 1px solid #EEF1F4 !important;
          }

          .excellent-kids-ranking-row:hover > td {
            background: #F8FAFC !important;
          }

          .ant-table-thead > tr > th {
            background: #F7F8FA !important;
            color: #687581 !important;
            font-size: 10px !important;
            font-weight: 800 !important;
            letter-spacing: 0.7px !important;
            border-bottom: 1px solid #E4E8EC !important;
          }

          .ant-table-tbody > tr:last-child > td {
            border-bottom: none !important;
          }

          .ant-select-selector,
          .ant-input {
            border-radius: 9px !important;
          }

          .ant-select-focused .ant-select-selector,
          .ant-input:focus {
            border-color: #C9A44C !important;
            box-shadow: 0 0 0 2px rgba(201,164,76,0.10) !important;
          }

          .ant-pagination-item-active {
            border-color: #C9A44C !important;
          }

          .ant-pagination-item-active a {
            color: #9C7930 !important;
          }
        `}
      </style>

    </div>
  );
};


export default TopStudentPerPeriod;